import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Building2, Check, Eye, EyeOff, Home, LockKeyhole, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { addHome } from "@/lib/homes.functions";
import { ShieldsLogo } from "@/components/brand/ShieldsLogo";
import { getSessionInfo } from "@/lib/session.functions";
import { postAuthDestination, safePostAuthRedirect, type AccountKind } from "@/lib/auth-destination";
import { checkAreaAvailability, type AreaAvailabilityResult } from "@/lib/areas.functions";

const REDIRECT_STORAGE_KEY = "shields:postAuthRedirect";
const SIGNUP_KIND_KEY = "shields:signupKind";
const AREA_HANDOFF_KEY = "shields:area-handoff";
const VERIFIED_SIGNUP_KEY = "shields:verified-area-signup";

function normalizePostcode(value: string) {
  const compact = value.toUpperCase().replace(/\s+/g, "");
  return compact.length > 3 ? `${compact.slice(0, -3)} ${compact.slice(-3)}` : compact;
}

type VerifiedSignup = { audience: AccountKind; postcode: string; email: string; checkedAt: number };
function readVerifiedSignup(): VerifiedSignup | null {
  if (typeof window === "undefined") return null;
  try {
    const value = JSON.parse(window.sessionStorage.getItem(VERIFIED_SIGNUP_KEY) ?? "null") as VerifiedSignup | null;
    if (!value || Date.now() - value.checkedAt > 15 * 60 * 1000) return null;
    return value;
  } catch { return null; }
}

function isUkPostcode(value: string) {
  return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(value.trim());
}

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string; intent?: AccountKind; mode?: "signup" } => {
    const redirect = safePostAuthRedirect(search["redirect"]);
    const intent = search["intent"] === "partner" ? "partner" : search["intent"] === "customer" ? "customer" : undefined;
    const mode = search["mode"] === "signup" ? "signup" : undefined;
    return { ...(redirect ? { redirect } : {}), ...(intent ? { intent } : {}), ...(mode ? { mode } : {}) };
  },
  head: () => ({
    meta: [
      { title: "Sign in — Shields" },
      { name: "description", content: "Secure access to your Shields Customer or Partner workspace." },
    ],
  }),
  component: AuthPage,
});

type Mode = "signin" | "signup" | "forgot";

function AuthPage() {
  const search = Route.useSearch();
  const [verifiedSignup] = useState<VerifiedSignup | null>(() => readVerifiedSignup());
  const [mode, setMode] = useState<Mode>(search.mode === "signup" ? "signup" : "signin");
  const [signupStep, setSignupStep] = useState<1 | 2>(1);
  const [accountKind, setAccountKind] = useState<AccountKind>(search.intent ?? verifiedSignup?.audience ?? "customer");
  const [email, setEmail] = useState(verifiedSignup?.email ?? "");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [postcode, setPostcode] = useState(verifiedSignup?.postcode ?? "");
  const [addressLine, setAddressLine] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [primaryTrade, setPrimaryTrade] = useState("");
  const [operatingRadius, setOperatingRadius] = useState("25");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const { redirect } = search;

  useEffect(() => {
    if (!redirect) return;
    try { window.localStorage.setItem(REDIRECT_STORAGE_KEY, redirect); } catch { /* storage can be unavailable in private/preview contexts */ }
  }, [redirect]);

  async function finishAuth() {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData.user) throw new Error("Your session could not be verified. Please sign in again.");

    // The server owns the role decision. The signup tab and browser metadata
    // never grant access to Owner or Partner screens.
    const session = await getSessionInfo();
    if (session.userId !== authData.user.id) throw new Error("Your session changed. Please sign in again.");

    let storedRedirect: string | undefined;
    try {
      storedRedirect = safePostAuthRedirect(window.localStorage.getItem(REDIRECT_STORAGE_KEY));
    } catch { /* browser storage is optional */ }

    const destination = postAuthDestination({
      kind: accountKind,
      session,
      requested: redirect,
      stored: storedRedirect,
    });
    try {
      window.localStorage.removeItem(REDIRECT_STORAGE_KEY);
      window.localStorage.removeItem(SIGNUP_KIND_KEY);
    } catch { /* navigation must work without storage */ }

    toast.success("Securely signed in");
    await navigate({ to: destination, replace: true });
  }

  async function resendConfirmation() {
    if (!unconfirmedEmail) return;
    setBusy(true);
    const { error } = await supabase.auth.resend({ type: "signup", email: unconfirmedEmail });
    setBusy(false);
    if (error) setError(error.message);
    else {
      setError(null);
      setNotice("Confirmation email sent. Check your inbox and spam folder.");
    }
  }

  function changeMode(next: Mode) {
    setMode(next);
    setError(null);
    setNotice(null);
    setUnconfirmedEmail(null);
    setSignupStep(1);
  }

  function continueSignup() {
    setError(null);
    if (!fullName.trim()) { setError("Add your full name to continue."); return; }
    if (!email.includes("@")) { setError("Enter a valid email address to continue."); return; }
    setSignupStep(2);
  }

  async function continueWithProvider(provider: "google" | "apple") {
    setBusy(true);
    setError(null);
    try {
      window.localStorage.setItem(SIGNUP_KIND_KEY, accountKind);
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin + (accountKind === "partner" ? "/partner/apply" : "/customer"),
        },
      });
      if (error) throw error;
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : "Could not start secure sign in.");
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setNotice("If an account exists for that email, a reset link is on its way.");
        return;
      }

      if (mode === "signup") {
        const handle = username.trim().toLowerCase();
        if (!/^[a-z0-9_]{3,20}$/.test(handle)) {
          throw new Error("Choose a username using 3–20 letters, numbers or underscores.");
        }
        if (!isUkPostcode(postcode)) {
          throw new Error("Enter a valid UK postcode so Shields can check local availability.");
        }
        if (!acceptedTerms) throw new Error("Please accept the Shields terms and privacy notice.");
        if (accountKind === "partner" && (!businessName.trim() || !primaryTrade.trim())) {
          throw new Error("Add your business name and primary trade.");
        }

        const formattedPostcode = normalizePostcode(postcode);
        const area = await checkAreaAvailability({ data: { postcode: formattedPostcode, email, audience: accountKind } });
        if (area.kind === "invalid") throw new Error(area.message);
        if (area.kind === "waitlist") {
          if (typeof window !== "undefined") {
            const handoff: { audience: AccountKind; postcode: string; email: string; result: AreaAvailabilityResult } = { audience: accountKind, postcode: formattedPostcode, email, result: area };
            window.sessionStorage.setItem(AREA_HANDOFF_KEY, JSON.stringify(handoff));
            window.location.assign("/availability?path=" + accountKind);
          }
          return;
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName.trim(),
              username: handle,
              registration_intent: accountKind,
            },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;

        window.localStorage.setItem(SIGNUP_KIND_KEY, accountKind);
        window.sessionStorage.removeItem(VERIFIED_SIGNUP_KEY);

        if (!data.session) {
          setNotice(
            accountKind === "partner"
              ? "Confirm your email, then sign in to continue your Partner application."
              : "Confirm your email, then sign in to finish setting up your home.",
          );
          setMode("signin");
          return;
        }

        if (accountKind === "customer") {
          const saved = await addHome({
            data: {
              label: "Home",
              postcode: formattedPostcode,
              addressLine: addressLine.trim() || undefined,
            },
          });
          if (!saved.ok) {
            setNotice("Your account is ready. You can add the full home address securely from My Home.");
          }
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          if (error.message.toLowerCase().includes("email not confirmed")) {
            setUnconfirmedEmail(email);
            throw new Error("Your email is not confirmed yet. Use the link in your inbox or resend it below.");
          }
          throw error;
        }
      }
      await finishAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className={`auth-experience min-h-screen px-4 py-5 text-[#071a3d] sm:px-6 ${accountKind === "partner" && mode === "signin" ? "partner-signin-reference" : "lg:grid lg:grid-cols-[minmax(22rem,.82fr)_minmax(28rem,1.18fr)] lg:gap-8 lg:px-8 lg:py-8"}`}>
      <section className="auth-brand-stage relative hidden overflow-hidden rounded-[28px] border border-white/80 bg-[linear-gradient(155deg,#09234b,#0d2f63)] p-9 text-white shadow-[0_30px_90px_-45px_rgba(9,35,75,.8)] lg:flex lg:min-h-[calc(100vh-4rem)] lg:flex-col">
        <div className="absolute -right-28 top-20 h-80 w-80 rounded-full bg-[#69baf0]/20 blur-3xl" aria-hidden />
        <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-[#d7ae65]/15 blur-3xl" aria-hidden />
        <Link to="/" aria-label="Shields home" className="auth-logo-beacon relative w-fit overflow-hidden rounded-xl bg-white/95 p-3 shadow-sm">
          <ShieldsLogo tone="dark" />
        </Link>
        <div className="relative my-auto max-w-xl">
          <p className="text-[11px] font-bold tracking-[0.18em] text-[#bfe8ff]">LOCAL SERVICES, REIMAGINED.</p>
          <h1 className="mt-5 font-display text-5xl font-extrabold leading-[1.02] tracking-[-0.045em]">
            One secure place for your home or business.
          </h1>
          <p className="mt-5 max-w-lg text-base leading-7 text-white/75">
            Customers get a calm, guided way to arrange work. Approved Partners get a professional workspace to find and run it.
          </p>
          <div className="mt-8 grid gap-3 text-sm text-white/85">
            {[
              "Privacy-safe local matching",
              "Clear GBP offers and protected workflows",
              "Partner access only after human approval",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[.06] px-4 py-3">
                <Check className="h-4 w-4 text-[#e8c987]" aria-hidden />
                {item}
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-white/55">Shields never exposes your exact address before an authorised booking stage.</p>
      </section>

      <section className="mx-auto flex w-full max-w-2xl items-center justify-center py-4 lg:py-8">
        <div className="w-full">
          <div className="auth-mobile-intro lg:hidden">
            <div className="auth-mobile-emblem">
              <Link to="/" aria-label="Shields home" className="auth-logo-beacon">
                <ShieldsLogo showWordmark={false} />
              </Link>
              <span className="auth-mobile-light" aria-hidden />
            </div>
            <div>
              <p>SHIELDS · YOUR HOME, HANDLED.</p>
              <strong>{mode === "signup" ? (signupStep === 1 ? "Create your account" : "Choose how you’ll use Shields") : mode === "forgot" ? "Secure account recovery." : "Welcome back to Shields."}</strong>
            </div>
          </div>

          <div key={mode + accountKind} className="auth-access-card rounded-[26px] border border-white/90 bg-white/75 p-5 shadow-[0_28px_80px_-48px_rgba(21,62,128,.55),inset_0_1px_rgba(255,255,255,.98)] backdrop-blur-2xl sm:p-8">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#e8c987]/50 bg-[#fffdf8] text-[#c9923e]">
                <LockKeyhole className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <p className="text-[10px] font-bold tracking-[0.16em] text-[#506782]">SECURE SHIELDS ACCESS</p>
                <h2 className="mt-1 font-display text-2xl font-extrabold tracking-[-0.03em]">
                  {mode === "signin" && (accountKind === "partner" ? "Welcome back to Shields Business" : "Welcome back to Shields Home")}
                  {mode === "signup" && "Create your account"}
                  {mode === "forgot" && "Reset your password"}
                </h2>
                <p className="mt-1 text-sm text-[#506782]">
                  {mode === "signin" && (accountKind === "partner" ? "Sign in to your professional workspace for opportunities, jobs, calendar, customers and money." : "Sign in to book work, track jobs and manage your home.")}
                  {mode === "signup" && "Join Shields in a few simple steps."}
                  {mode === "forgot" && "We will send a secure recovery link if the account exists."}
                </p>
              </div>
            </div>

            {mode === "signup" ? (
              <ol className="auth-journey" aria-label="Account setup progress">
                <li className="is-active"><b>01</b><span>Your account</span></li>
                <li className={signupStep === 2 ? "is-active" : ""}><b>02</b><span>Home or business</span></li>
                <li><b>03</b><span>Ready</span></li>
              </ol>
            ) : mode === "signin" ? (
              <>
                <fieldset className="mt-6">
                  <legend className="text-xs font-bold tracking-[0.12em] text-[#506782]">SIGN IN TO</legend>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {([
                      ["customer", Home, "Shields Home", "Customer"],
                      ["partner", Building2, "Shields Business", "Partner"],
                    ] as const).map(([kind, Icon, title, subtitle]) => (
                      <button
                        key={kind}
                        type="button"
                        aria-pressed={accountKind === kind}
                        onClick={() => {
                          setAccountKind(kind);
                          setError(null);
                          setNotice(null);
                        }}
                        className={`min-h-24 rounded-2xl border px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#153e80] ${accountKind === kind ? "border-[#78aef3] bg-[#f4f8ff] shadow-sm ring-1 ring-[#78aef3]/30" : "border-[#dce9f3] bg-white hover:bg-[#fbfdff]"}`}
                      >
                        <Icon className="h-5 w-5 text-[#153e80]" aria-hidden />
                        <strong className="mt-2 block text-sm text-[#071a3d]">{title}</strong>
                        <span className="mt-0.5 block text-xs text-[#71869d]">{subtitle}</span>
                      </button>
                    ))}
                  </div>
                </fieldset>
                <div className="auth-trust-strip" aria-label="Shields access protections">
                  <span><ShieldCheck aria-hidden /> Secure session</span>
                  <span><LockKeyhole aria-hidden /> Private by design</span>
                </div>
              </>
            ) : (
              <div className="auth-trust-strip" aria-label="Shields access protections">
                <span><ShieldCheck aria-hidden /> Secure recovery</span>
                <span><LockKeyhole aria-hidden /> Private by design</span>
              </div>
            )}

            {mode === "signup" && signupStep === 2 ? (
              <fieldset className="mt-6">
                <legend className="text-xs font-bold tracking-[0.12em] text-[#506782]">HOW WOULD YOU LIKE TO USE SHIELDS?</legend>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {([
                    ["customer", Home, "I need home services", "Book trusted professionals for your home"],
                    ["partner", Building2, "I’m a tradesperson", "Join our network of trusted professionals"],
                  ] as const).map(([kind, Icon, title, subtitle]) => (
                    <button
                      key={kind}
                      type="button"
                      aria-pressed={accountKind === kind}
                      onClick={() => setAccountKind(kind)}
                      className={`auth-kind-choice min-h-28 rounded-2xl border px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#153e80] ${accountKind === kind ? "border-[#78aef3] bg-[#f4f8ff] shadow-sm ring-1 ring-[#78aef3]/30" : "border-[#dce9f3] bg-white hover:bg-[#fbfdff]"}`}
                    >
                      <Icon className="h-5 w-5 text-[#153e80]" aria-hidden />
                      <strong className="mt-3 block text-base text-[#071a3d]">{title}</strong>
                      <span className="mt-1 block text-sm leading-5 text-[#71869d]">{subtitle}</span>
                    </button>
                  ))}
                </div>
              </fieldset>
            ) : null}

            {error ? (
              <div role="alert" className="mt-5 rounded-2xl border border-[#e66767]/30 bg-[#fff7f7] px-4 py-3 text-sm text-[#8f3434]">
                <strong className="block">Please check the details</strong>
                <span>{error}</span>
              </div>
            ) : null}
            {notice ? (
              <p role="status" className="mt-5 rounded-2xl border border-[#32d6aa]/30 bg-[#f3fffb] px-4 py-3 text-sm font-semibold text-[#176a56]">
                {notice}
              </p>
            ) : null}
            {unconfirmedEmail ? (
              <Button type="button" variant="outline" onClick={resendConfirmation} disabled={busy} className="mt-3 min-h-11 w-full">
                Resend confirmation email
              </Button>
            ) : null}

            {mode === "signup" && signupStep === 1 && accountKind === "customer" && (
              <div className="mt-6 grid gap-2">
                <Button type="button" variant="outline" disabled={busy} onClick={() => continueWithProvider("apple")} className="min-h-12 rounded-xl bg-black font-bold text-white hover:bg-black/90 hover:text-white">
                  Continue with Apple
                </Button>
                <Button type="button" variant="outline" disabled={busy} onClick={() => continueWithProvider("google")} className="min-h-12 rounded-xl font-bold">
                  Continue with Google
                </Button>
                <div className="flex items-center gap-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#8a98a8]">
                  <span className="h-px flex-1 bg-[#dce6ef]" /> or sign up with email <span className="h-px flex-1 bg-[#dce6ef]" />
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form mt-6 grid gap-4 sm:grid-cols-2">
              {mode === "signin" ? (
                <>
                  <Field label="Email address" id="email" wide>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoCapitalize="none" spellCheck={false} required className="min-h-12 rounded-xl" />
                  </Field>
                  <Field label="Password" id="password" wide>
                    <PasswordField id="password" value={password} setValue={setPassword} show={showPassword} setShow={setShowPassword} autoComplete="current-password" />
                  </Field>
                  <Button type="submit" disabled={busy} className="auth-primary-action col-span-full min-h-14 rounded-xl text-base font-bold">
                    {busy ? "Signing in…" : accountKind === "partner" ? "Sign in to Shields Business" : "Sign in to Shields Home"}
                  </Button>
                </>
              ) : mode === "forgot" ? (
                <>
                  <Field label="Email address" id="email" wide>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required className="min-h-12 rounded-xl" />
                  </Field>
                  <Button type="submit" disabled={busy} className="auth-primary-action col-span-full min-h-14 rounded-xl text-base font-bold">
                    {busy ? "Sending…" : "Send secure reset link"}
                  </Button>
                </>
              ) : signupStep === 1 ? (
                <>
                  <Field label="Full name" id="full-name" wide>
                    <Input id="full-name" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" required className="min-h-12 rounded-xl" />
                  </Field>
                  <Field label="Email address" id="signup-email" wide>
                    <Input id="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoCapitalize="none" spellCheck={false} required className="min-h-12 rounded-xl" />
                  </Field>
                  <Button type="button" onClick={continueSignup} className="auth-primary-action col-span-full min-h-14 rounded-xl text-base font-bold">
                    Continue
                  </Button>
                  <p className="auth-step-note col-span-full">Next, we’ll securely check your area and collect only the details needed for your {accountKind === "partner" ? "business" : "home"}.</p>
                </>
              ) : (
                <>
                  <Field label="Username" id="username" wide>
                    <Input id="username" value={username} onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase())} autoComplete="username" required minLength={3} maxLength={20} placeholder="yourname" className="min-h-12 rounded-xl" />
                  </Field>
                  {accountKind === "partner" ? (
                    <>
                      <Field label="Business or trading name" id="business-name">
                        <Input id="business-name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required className="min-h-12 rounded-xl" />
                      </Field>
                      <Field label="Primary trade" id="primary-trade">
                        <Input id="primary-trade" value={primaryTrade} onChange={(e) => setPrimaryTrade(e.target.value)} placeholder="e.g. Plumbing" required className="min-h-12 rounded-xl" />
                      </Field>
                    </>
                  ) : (
                    <Field label="Address line (optional and private)" id="address-line" wide>
                      <Input id="address-line" value={addressLine} onChange={(e) => setAddressLine(e.target.value)} autoComplete="street-address" placeholder="You can add this later" className="min-h-12 rounded-xl" />
                    </Field>
                  )}
                  <Field label={accountKind === "partner" ? "Operating base postcode" : "Home postcode"} id="postcode">
                    <div className="relative">
                      <MapPin className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-[#71869d]" aria-hidden />
                      <Input id="postcode" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} autoComplete="postal-code" placeholder="BT26 6AG" required className="min-h-12 rounded-xl pl-10" />
                    </div>
                  </Field>
                  {accountKind === "partner" ? (
                    <Field label="Operating radius (miles)" id="operating-radius">
                      <Input id="operating-radius" type="number" min={1} max={100} value={operatingRadius} onChange={(e) => setOperatingRadius(e.target.value)} required className="min-h-12 rounded-xl" />
                    </Field>
                  ) : null}
                  <Field label="Create password" id="password" wide>
                    <PasswordField id="password" value={password} setValue={setPassword} show={showPassword} setShow={setShowPassword} autoComplete="new-password" />
                  </Field>
                  <label className="auth-terms col-span-full">
                    <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />
                    <span>I agree to the Shields terms and privacy notice. My postcode checks availability; my exact address stays private.</span>
                  </label>
                  <button type="button" onClick={() => { setSignupStep(1); setError(null); }} className="auth-back-action">← Back</button>
                  <Button type="submit" disabled={busy} className="auth-primary-action min-h-14 rounded-xl text-base font-bold">
                    {busy ? "Creating account…" : accountKind === "partner" ? "Create account and apply" : "Create Customer account"}
                  </Button>
                </>
              )}
            </form>

            {mode === "signin" ? (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#dce9f3] bg-[#f8fbff] px-3 py-2.5 text-xs leading-5 text-[#506782]">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#c9923e]" aria-hidden />
                <span>Shields securely keeps your session signed in on this device. Your browser or password manager can remember the password; Shields never stores the raw password.</span>
              </div>
            ) : null}

            <div className="mt-6 space-y-2 text-center text-sm">
              {mode === "signin" ? (
                <>
                  <button type="button" onClick={() => changeMode("forgot")} className="block min-h-11 w-full font-semibold text-[#153e80] hover:underline">Forgot your password?</button>
                  <p className="text-[#506782]">New to Shields? <button type="button" onClick={() => changeMode("signup")} className="font-bold text-[#153e80] hover:underline">Create an account</button></p>
                </>
              ) : (
                <button type="button" onClick={() => changeMode("signin")} className="min-h-11 font-semibold text-[#153e80] hover:underline">Back to sign in</button>
              )}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-center gap-2 text-center text-xs text-[#71869d]">
            <ShieldCheck className="h-4 w-4 text-[#c9923e]" aria-hidden />
            Owner access is never offered publicly. Partner tools require approval.
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({
  label,
  id,
  wide = false,
  children,
}: {
  label: string;
  id: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label htmlFor={id} className="text-xs font-bold tracking-[0.05em] text-[#09234b]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


function PasswordField({ id, value, setValue, show, setShow, autoComplete }: { id: string; value: string; setValue: (value: string) => void; show: boolean; setShow: (value: boolean) => void; autoComplete: "current-password" | "new-password" }) {
  return (
    <div className="relative">
      <Input id={id} type={show ? "text" : "password"} value={value} onChange={(e) => setValue(e.target.value)} autoComplete={autoComplete} minLength={8} required className="min-h-12 rounded-xl pr-12" />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} aria-pressed={show} className="absolute inset-y-0 right-0 flex min-w-11 items-center justify-center rounded-r-xl text-[#506782] hover:text-[#09234b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#153e80]">
        {show ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
      </button>
    </div>
  );
}
